import {
  cloneElement,
  FunctionComponentElement,
  MutableRefObject,
} from "react";
import { THEME_COLOR_DARK_ALT } from "../vars";
import { useTooltip, TooltipPopup, Position } from "@reach/tooltip";
import "@reach/tooltip/styles.css";

// This comes from an un-exported type in @reach/tooltip
type Rect =
  | null
  | undefined
  | (Partial<DOMRect> & {
      readonly bottom: number;
      readonly height: number;
      readonly left: number;
      readonly right: number;
      readonly top: number;
      readonly width: number;
    });

const centered: Position = (triggerRect: Rect, tooltipRect: Rect) => {
  if (!triggerRect) return { top: 0, left: 0 };
  if (!tooltipRect) return { top: 0, left: 0 };

  const triggerCenterX = triggerRect.left + triggerRect.width / 2;
  const triggerCenterY = triggerRect.top + triggerRect.height / 2;
  const left = triggerCenterX + triggerRect.width / 2 + 12;
  const top = triggerCenterY - tooltipRect.height / 2;
  const maxLeft = window.innerWidth - tooltipRect.width - 2;
  const maxTop = window.innerHeight - tooltipRect.height - 2;
  return {
    left: Math.min(Math.max(2, left), maxLeft) + window.scrollX,
    top: Math.min(Math.max(2, top), maxTop) + window.scrollY,
  };
};

function mergeRefs<T = any>(
  refs: Array<React.MutableRefObject<T> | React.LegacyRef<T>>
): React.RefCallback<T> {
  return (value) => {
    refs.forEach((ref) => {
      if (typeof ref === "function") {
        ref(value);
      } else if (ref !== null) {
        (ref as React.MutableRefObject<T | null>).current = value;
      }
    });
  };
}

export default function TooltipRight({
  children,
  disabled,
  label,
  ariaLabel = "3",
}: {
  children: FunctionComponentElement<any>;
  disabled?: boolean;
  label: string;
  ariaLabel?: string;
}) {
  const [trigger, tooltip] = useTooltip();
  const { triggerRect } = tooltip;

  return (
    <>
      {cloneElement(children, {
        ...trigger,
        ...children.props,
        ref: mergeRefs([children.ref as MutableRefObject<any>, trigger.ref]),
      })}
      {!disabled && triggerRect && (
        <>
          <TooltipPopup
            {...tooltip}
            label={label}
            ariaLabel={ariaLabel}
            style={{
              background: `${THEME_COLOR_DARK_ALT}`,
              color: "white",
              border: "none",
              borderRadius: "3px",
              padding: "0.5em 1em",
              zIndex: 10000000000,
              pointerEvents: "none",
              fontSize: "14px",
            }}
            position={centered}
          />
        </>
      )}
    </>
  );
}
